"use client";

import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  MotionValue,
} from "framer-motion";
import { Quote } from "lucide-react";

const cardGradients = [
  "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
  "linear-gradient(135deg, #2d2d2d 0%, #404040 100%)",
  "linear-gradient(135deg, #1f1f1f 0%, #383838 100%)",
];

const testimonials = [
  // {
  //   id: "t1",
  //   quote:
  //     "Zysoftec truly EXCELS in software development, delivering bug-free code with unmatched expertise and exceeding all expectations. Their politeness and proactive communication made the experience seamless, and I really appreciated their honesty and dedication. One of the best development teams you'll encounter.",
  //   author: {
  //     name: "Trumely",
  //     title: "CEO",
  //     companyType: "Technology Business",
  //     location: "United States",
  //   },
  //   tags: ["Software Development", "Communication", "Quality"],
  // },

  {
    id: "t2",
    quote:
      "Zill and her team were incredibly professional, patient, and committed throughout the project. We shared our requirements, and they transformed them into a solution that exceeded expectations. We truly appreciate their clean work and attention to detail.",
    author: {
      name: "Ali",
      title: "Business Owner",
      companyType: "Retail & Commerce",
      location: "Libya",
    },
    tags: ["Professionalism", "Execution", "Collaboration"],
  },

  {
    id: "t3",
    quote:
      "Zysoftec delivered an exceptional experience in software development, surpassing expectations with professionalism and flawless execution. The team's responsiveness, cooperative spirit, and dedication made collaboration effortless. Looking forward to working together again.",
    author: {
      name: "Alaa",
      title: "Founder",
      companyType: "Digital Business",
      location: "Egypt",
    },
    tags: ["Professional", "Responsive", "Reliable"],
  },

  {
    id: "t4",
    quote:
      "Zysoftec delivered top-notch work from start to finish. Their communication was clear, their responses were quick, and they consistently demonstrated a cooperative attitude. I highly recommend them for software development projects.",
    author: {
      name: "Abhijeeth",
      title: "Project Manager",
      companyType: "Technology Services",
      location: "India",
    },
    tags: ["Communication", "Development", "Delivery"],
  },

  {
    id: "t5",
    quote:
      "Great experience working with the team. They helped complete the first stage of our project smoothly and provided valuable guidance for future development. If you're looking for a hardworking full-stack development team that also offers strategic advice, Zysoftec is an excellent choice.",
    author: {
      name: "Eddie",
      title: "Founder",
      companyType: "Startup",
      location: "United Kingdom",
    },
    tags: ["Full Stack", "Startup", "Advisory"],
  },
];

interface TestimonialCardProps {
  i: number;
  testimonial: (typeof testimonials)[0];
  progress: MotionValue<number>;
  totalCards: number;
}

function TestimonialCard({
  i,
  testimonial,
  progress,
  totalCards,
}: TestimonialCardProps) {
  const cardStart = i / totalCards;
  const cardEnd = (i + 1) / totalCards;

  const y = useTransform(
    progress,
    [cardStart, cardEnd],
    ["100%", "0%"]
  );

  const opacity = useTransform(
    progress,
    [cardStart, cardStart + 0.05, cardEnd - 0.05, cardEnd],
    [0, 1, 1, 1]
  );

  return (
    <motion.div
      style={{
        y: i === 0 ? 0 : y,
        opacity: i === 0 ? 1 : opacity,
        background: cardGradients[i % cardGradients.length],
        boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        zIndex: i + 1,
      }}
      className="absolute inset-0 rounded-2xl p-5 md:p-8"
    >
      {/* Background Quote */}
      <div className="absolute right-6 md:right-10 top-1/2 -translate-y-1/2 opacity-[0.08]">
        <Quote
          className="w-48 h-48 md:w-64 md:h-64 text-white"
          strokeWidth={1}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 h-full relative z-10">
        {/* Left Content */}
        <div className="md:col-span-7 flex flex-col justify-center">
          <p className="text-[15px] md:text-[16px] text-white leading-relaxed whitespace-pre-line">
            "{testimonial.quote}"
          </p>

          <div className="flex flex-wrap gap-2 mt-6">
            {testimonial.tags.map((tag, idx) => (
              <span
                key={idx}
                className="text-[11px] px-3 py-1 rounded-full"
                style={{
                  background: "rgba(255,255,255,0.12)",
                  color: "#ffffff",
                  fontWeight: 500,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Right Meta */}
        <div className="md:col-span-5 flex flex-col justify-center border-t md:border-t-0 md:border-l border-white/20 pt-6 md:pt-0 md:pl-8">
          <div className="space-y-5">
            <div>
              <p className="text-[10px] text-white/50 uppercase tracking-wider mb-1">
                Name
              </p>
              <p className="text-white text-[15px] font-medium">
                {testimonial.author.name}
              </p>
            </div>

            <div>
              <p className="text-[10px] text-white/50 uppercase tracking-wider mb-1">
                Role
              </p>
              <p className="text-white text-[15px] font-medium">
                {testimonial.author.title}
              </p>
            </div>

            <div>
              <p className="text-[10px] text-white/50 uppercase tracking-wider mb-1">
                Industry
              </p>
              <p className="text-white/90 text-[14px]">
                {testimonial.author.companyType}
              </p>
            </div>

            <div>
              <p className="text-[10px] text-white/50 uppercase tracking-wider mb-1">
                Location
              </p>
              <p className="text-white/90 text-[14px]">
                {testimonial.author.location}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function ProgressDot({
  index,
  progress,
  totalCards,
}: {
  index: number;
  progress: MotionValue<number>;
  totalCards: number;
}) {
  const isActive = useTransform(progress, (value) => {
    const current = value * totalCards;
    return current >= index && current < index + 1;
  });

  const width = useTransform(
    isActive,
    (active) => (active ? "2rem" : "0.5rem")
  );

  const backgroundColor = useTransform(
    isActive,
    (active) => (active ? "#141414" : "#D1D5DB")
  );

  return (
    <motion.div
      className="h-2 rounded-full"
      style={{
        width,
        backgroundColor,
      }}
    />
  );
}

export default function Testimonials() {
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  return (
    <section
      ref={containerRef}
      className="relative bg-[#F5F5F7]"
      style={{
        height: `${100 + testimonials.length * 50}vh`,
      }}
    >
      <div className="sticky top-0 h-screen flex flex-col items-center justify-center px-6 md:px-12">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="text-center max-w-[700px] w-full mb-8"
        >
          <h2
            className="text-[28px] sm:text-[34px] md:text-[40px] text-[#141414] mb-4"
            style={{
              fontWeight: 500,
              lineHeight: 1.18,
              letterSpacing: "-0.015em",
            }}
          >
            Trusted by Founders & Growing Businesses
          </h2>

          <p
            className="text-[15px] md:text-[16px] text-[#4B5563]"
            style={{
              fontWeight: 400,
              lineHeight: 1.6,
            }}
          >
            From startup MVPs to enterprise platforms, we help teams build
            software that scales with confidence.
          </p>
        </motion.div>

        {/* Testimonial Cards */}
        <div className="relative max-w-[1100px] h-[560px] sm:h-[460px] md:h-[340px] w-full overflow-hidden rounded-2xl">
          {testimonials.map((testimonial, i) => (
            <TestimonialCard
              key={testimonial.id}
              i={i}
              testimonial={testimonial}
              progress={scrollYProgress}
              totalCards={testimonials.length}
            />
          ))}
        </div>

        {/* Progress Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {testimonials.map((_, index) => (
            <ProgressDot
              key={index}
              index={index}
              progress={scrollYProgress}
              totalCards={testimonials.length}
            />
          ))}
        </div>
      </div>
    </section>
  );
}